<script setup lang="ts">
import type { AccordionItem, AccordionProps } from '#ui/types';
import lodashHead from 'lodash-es/head';
import lodashOmit from 'lodash-es/omit';
import type { PropType } from 'vue';

// TYPES
type BaseAccordionSize = 'md' | 'lg' | 'xl';

export type BaseAccordionItem = AccordionItem & { defaultOpen?: boolean };

// PROPS
const { size, defaultOpen, type, items } = defineProps({
    items: {
        type: Array as PropType<BaseAccordionItem[]>,
        required: true,
    },
    defaultOpen: {
        type: Boolean,
        default: false,
    },
    type: {
        type: String as PropType<AccordionProps['type']>,
        default: undefined,
    },
    size: {
        type: String as PropType<BaseAccordionSize>,
        default: 'lg',
    },
});

// DATA
const SIZE_VARIANT_UI: Record<BaseAccordionSize, Record<string, string>> = {
    md: {
        trigger: 'font-normal text-base',
    },
    lg: {
        trigger: 'font-medium text-base',
    },
    xl: {
        trigger: 'font-medium md:text-2xl text-xl',
        body: 'text-base',
    },
};
const ui = computed(() => SIZE_VARIANT_UI[size]);
const defaultValue = computed(() => {
    const openedValues = items
        .map((i, index) => ({ ...i, index: String(index) }))
        .filter(i => defaultOpen || i.defaultOpen)
        .map(i => i.index);

    if (type === 'multiple') return openedValues;

    return lodashHead(openedValues) ?? (defaultOpen ? '0' : undefined);
});
const accordionItems = computed<AccordionItem[]>(() => items.map(i => lodashOmit(i, 'defaultOpen')));
</script>

<template>
    <UAccordion :items="accordionItems" :default-value="defaultValue" :ui="ui" :type="type">
        <template v-for="(_, slot) of $slots" #[slot]="scope"><slot v-if="slot !== 'trailing'" :name="slot" v-bind="scope" /></template>
        <template #trailing="{ open }">
            <span class="ms-auto inline-flex gap-2">
                <slot name="trailingIcon" />
                <UIcon :name="open ? 'lucide:minus' : 'lucide:plus'" class="size-5 shrink-0" />
            </span>
        </template>
    </UAccordion>
</template>
